import React, { useState } from "react";
import AdminDataTable from "../components/AdminDataTable";
import Button from "../../../components/ui/Button";
import Modal from "../../../components/ui/Modal";
import Input from "../../../components/ui/Input";
import ImageUpload from "../../../components/ui/ImageUpload";
import { PlusCircle, Edit, Trash2, Package, Search } from "lucide-react";
import { mockCreator, featuredCreators } from "../../../data/mockData";

// Mock initial inventory data (combining creator merch for admin view)
const initialInventory = mockCreator.merchandise.map(item => ({
  ...item,
  creatorName: mockCreator.fullName,
  sku: `SKU-${item.id + 1000}`,
  status: item.stock > 0 ? "In Stock" : "Out of Stock"
}));

const InventoryPage = () => {
  const [inventory, setInventory] = useState(initialInventory);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    creatorName: "",
    priceETB: "",
    stock: "",
    sku: "",
    imageUrl: ""
  });

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredInventory = inventory.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.creatorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenModal = (item = null) => {
    if (item) {
      setCurrentItem(item);
      setFormData({
        name: item.name,
        creatorName: item.creatorName,
        priceETB: item.priceETB,
        stock: item.stock,
        sku: item.sku,
        imageUrl: item.imageUrl
      });
    } else {
      setCurrentItem(null);
      setFormData({
        name: "",
        creatorName: "",
        priceETB: "",
        stock: "",
        sku: "",
        imageUrl: ""
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (currentItem) {
      // Edit
      setInventory(inventory.map(item => 
        item.id === currentItem.id 
          ? { ...item, ...formData, status: Number(formData.stock) > 0 ? "In Stock" : "Out of Stock" } 
          : item
      ));
    } else {
      // Create
      const newItem = {
        id: Date.now(),
        ...formData,
        status: Number(formData.stock) > 0 ? "In Stock" : "Out of Stock",
        sales: 0,
        imageUrl: "https://placehold.co/200x200/334155/FFFFFF?text=New+Item"
      };
      setInventory([...inventory, newItem]);
    }
    setIsModalOpen(false);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      setInventory(inventory.filter(item => item.id !== id));
    }
  };

  const columns = [
    {
      header: "Product",
      key: "name",
      render: (row) => (
        <div className="flex items-center">
          <img src={row.imageUrl} alt={row.name} className="w-10 h-10 rounded-md mr-3 object-cover" />
          <div>
            <p className="font-medium text-slate-800">{row.name}</p>
            <p className="text-xs text-slate-500">SKU: {row.sku}</p>
          </div>
        </div>
      ),
    },
    {
      header: "Creator",
      key: "creatorName",
      render: (row) => <span className="text-sm text-slate-600">{row.creatorName}</span>,
    },
    {
      header: "Price",
      key: "priceETB",
      render: (row) => <span className="font-medium">{row.priceETB.toLocaleString()} ETB</span>,
    },
    {
      header: "Stock",
      key: "stock",
      render: (row) => (
        <span className={`font-medium ${row.stock < 10 ? "text-red-600" : "text-slate-700"}`}>
          {row.stock} units
        </span>
      ),
    },
    {
      header: "Status",
      key: "status",
      render: (row) => (
        <span className={`px-2 py-1 text-xs font-bold rounded-full ${
          row.status === "In Stock" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
        }`}>
          {row.status}
        </span>
      ),
    },
    {
      header: "Actions",
      key: "actions",
      render: (row) => (
        <div className="flex space-x-2">
          <Button variant="ghost" size="icon" onClick={() => handleOpenModal(row)}>
            <Edit size={16} className="text-slate-500" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => handleDelete(row.id)}>
            <Trash2 size={16} className="text-red-500" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Inventory Management</h1>
          <p className="text-slate-500">Manage products and stock levels for creators.</p>
        </div>
        <Button onClick={() => handleOpenModal()}>
          <PlusCircle size={18} className="mr-2" /> Add Product
        </Button>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 flex items-center">
        <Search size={20} className="text-slate-400 mr-3" />
        <input
          type="text"
          placeholder="Search inventory by name, SKU, or creator..."
          className="flex-grow outline-none text-slate-700"
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>

      <AdminDataTable
        columns={columns}
        data={filteredInventory}
        title="Product Inventory"
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={currentItem ? "Edit Product" : "Add New Product"}
      >
        <div className="space-y-4">
          <Input
            label="Product Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g. Signature Hoodie"
          />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Creator</label>
            <select
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              value={formData.creatorName}
              onChange={(e) => setFormData({...formData, creatorName: e.target.value})}
              required
            >
              <option value="">Select Creator</option>
              {featuredCreators.map((creator) => (
                <option key={creator.id} value={creator.displayName}>
                  {creator.displayName} (@{creator.username})
                </option>
              ))}
            </select>
          </div>
          {/* Image Upload Simulation */}
          <div>
            <ImageUpload
              label="Product Image"
              currentImage={formData.imageUrl}
              onUpload={(file, url) =>
                setFormData({ ...formData, imageUrl: url })
              }
              aspectRatio="square"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Price (ETB)"
              type="number"
              value={formData.priceETB}
              onChange={(e) => setFormData({ ...formData, priceETB: Number(e.target.value) })}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Stock Quantity</label>
            <Input
              type="number"
              value={formData.stock}
              onChange={(e) => setFormData({...formData, stock: e.target.value})}
              placeholder="Available quantity"
              min="0"
              required
            />
            <p className="text-xs text-slate-500 mt-1">Number of items available in inventory</p>
          </div>
          <Input
            label="SKU"
            value={formData.sku}
            onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
            placeholder="e.g. SKU-1001"
          />
          <div className="flex justify-end space-x-2 mt-6">
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save Product</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InventoryPage;
